import moment from 'moment-timezone';

import { getExtraActivityLabel } from 'common/ActivityChip/utils/extraActivities';

const COLUMNS_IN_BASE_WIDTH = 2;
export const BASE_GRID_COLUMN_WIDTH = 138;

export const getGridTemplateColumns = (stationsCount) => {
	const columnsCount = Math.max(stationsCount - 1, 0) * COLUMNS_IN_BASE_WIDTH;

	if (columnsCount === 0) return '';

	return `repeat(${columnsCount}, minmax(${BASE_GRID_COLUMN_WIDTH}px, 1fr))`;
};

// requires moment-timezone
export const getDayEntriesFromStationsInTimezone = (stations, tz, format = 'DD/MM/YYYY') => {
	const toDate = (iso) => (iso == null ? null : moment.utc(iso).tz(tz).format(format));

	const datesMap = new Map();
	const addIfNew = (day, hasShiftOnStation = false) => {
		if (!day) return;
		if (!datesMap.has(day)) {
			datesMap.set(day, { day, stationCount: 0, hasShiftOnStation: false });
		} else {
			const prevData = datesMap.get(day);
			const { stationCount } = prevData;
			datesMap.set(day, { ...prevData, stationCount: stationCount + 1, hasShiftOnStation });
		}
	};

	for (let i = 0; i < stations.length; i++) {
		const { arrival, departure } = stations[i];
		const datedArrival = toDate(arrival);
		const datedDeparture = toDate(departure);

		const areSameDays = datedArrival === datedDeparture;

		if (areSameDays) {
			addIfNew(datedArrival);
		} else {
			addIfNew(datedArrival, true);
			addIfNew(datedDeparture);
		}
	}

	const entries = [...datesMap.values()];
	const toIterateLengt = entries.length - 1;
	const result = [];

	for (let i = 0; i <= toIterateLengt; i++) {
		const { day, stationCount, hasShiftOnStation } = entries[i];

		const fromColumn = i === 0 ? 1 : result[result.length - 1].toColumn;

		const toColumn = hasShiftOnStation
			? fromColumn + stationCount * COLUMNS_IN_BASE_WIDTH
			: fromColumn + stationCount * COLUMNS_IN_BASE_WIDTH + COLUMNS_IN_BASE_WIDTH / 2;

		result.push({
			date: day,
			fromColumn: fromColumn,
			toColumn: i === toIterateLengt ? toColumn + 1 : toColumn,
		});
	}

	return result;
};

export const caclStationColumnPlacement = (index, lastIndex) => {
	if (index === 0) return { from: 1, to: 2 };
	if (index === lastIndex)
		return {
			from: index * COLUMNS_IN_BASE_WIDTH,
			to: index * COLUMNS_IN_BASE_WIDTH + COLUMNS_IN_BASE_WIDTH / 2,
		};
	return {
		from: index * COLUMNS_IN_BASE_WIDTH,
		to: index * COLUMNS_IN_BASE_WIDTH + COLUMNS_IN_BASE_WIDTH,
	};
};

export const caclRegularColumnPlacement = (indexFrom, indexTo) => {
	return {
		from: indexFrom * COLUMNS_IN_BASE_WIDTH + COLUMNS_IN_BASE_WIDTH / 2,
		to: indexTo * COLUMNS_IN_BASE_WIDTH + COLUMNS_IN_BASE_WIDTH / 2,
	};
};

export const calcLanePlacement1 = (stationIndex, lastIndex) => {
	// lastIndex = stations.length - 1
	// lanes count = stations.length - 1

	const laneIndex = Math.min(stationIndex, lastIndex - 1);

	const from = laneIndex * COLUMNS_IN_BASE_WIDTH + COLUMNS_IN_BASE_WIDTH / 2;
	const to = (laneIndex + 1) * COLUMNS_IN_BASE_WIDTH + COLUMNS_IN_BASE_WIDTH / 2;

	return { from, to };
};

export const calcLanePlacement = (stationIndex, waFromIndex, waToIndex) => {
	// lanes exist for: waFromIndex .. waToIndex-1
	const lastLaneIndex = waToIndex - 1;

	// last station maps to last lane
	const clamped = Math.max(waFromIndex, Math.min(stationIndex, lastLaneIndex));

	const localLaneIndex = clamped - waFromIndex; // <-- THIS is the missing piece

	const from = localLaneIndex * 2 + 1;
	const to = from + 2;

	return { from, to };
};

const sortExtraActivityItems = (items) =>
	[...items].sort((x, y) => {
		if (x.stationIndex !== y.stationIndex) return x.stationIndex - y.stationIndex;

		const ax = x.fromMs ?? Number.POSITIVE_INFINITY;
		const ay = y.fromMs ?? Number.POSITIVE_INFINITY;
		if (ax !== ay) return ax - ay;

		return String(x.id).localeCompare(String(y.id));
	});

const buildExtraActivityStacksByLaneIndex = ({
	assignment,
	getStationById,
	lastStationIndex,
	stationTo,
}) => {
	const stacksByLaneIndex = new Map();

	(assignment.extraActivities ?? []).forEach((ea) => {
		const fromId = ea?.fromStation?.id;
		const toId = ea?.toStation?.id;

		const fromStation = fromId ? getStationById(fromId) : undefined;
		const toStation = toId ? getStationById(toId) : undefined;

		const fromIdx = fromStation?.index;
		const toIdx = toStation?.index;
		const stationIndex = Number.isFinite(fromIdx) ? fromIdx : toIdx;
		if (!Number.isFinite(stationIndex)) return;

		const laneIndex = Math.min(stationIndex, lastStationIndex - 1);
		const durationMinutes =
			ea?.fromTimestamp && ea?.toTimestamp
				? moment(ea.toTimestamp).diff(moment(ea.fromTimestamp), 'minutes')
				: 0;

		const label = getExtraActivityLabel(ea.activityType);
		const stationId = ea?.fromStation?.id ?? ea?.toStation?.id;
		const stationCode = ea?.fromStation?.code ?? ea?.toStation?.code;
		const isInLastStation = stationCode === stationTo.code;
		const fromMs = ea?.fromTimestamp ? moment(ea.fromTimestamp).valueOf() : null;

		const viewModel = {
			id: ea.id,
			version: ea.version,
			activityType: ea.activityType,
			status: ea.status ?? null,
			label,
			durationMinutes,
			stationId,
			stationCode,
			stationIndex,
			shouldShiftLeft: isInLastStation,
			fromMs,
		};

		const arr = stacksByLaneIndex.get(laneIndex) ?? [];
		arr.push(viewModel);
		stacksByLaneIndex.set(laneIndex, arr);
	});

	return stacksByLaneIndex;
};

const transformExtraActivityStacksByLaneIndex = ({
	stacksByLaneIndex,
	assignment,
	indexFrom,
	indexTo,
}) => {
	return Array.from(stacksByLaneIndex.entries())
		.sort(([a], [b]) => a - b)
		.map(([laneIndex, items]) => ({
			laneKey: `${assignment.storeId}-lane-${laneIndex}`,
			placement: calcLanePlacement(laneIndex, indexFrom, indexTo),
			items: sortExtraActivityItems(items),
		}));
};

export const getAssignmentExtraActivityLaneStacks = ({
	assignment,
	getStationById,
	lastStationIndex,
	indexFrom,
	indexTo,
	stationTo,
}) => {
	const stacksByLaneIndex = buildExtraActivityStacksByLaneIndex({
		assignment,
		getStationById,
		lastStationIndex,
		stationTo,
	});

	return transformExtraActivityStacksByLaneIndex({
		stacksByLaneIndex,
		assignment,
		indexFrom,
		indexTo,
	});
};
